/**
 * PremiumDashboard - Par Seeker Premium Experience
 * High-end, pay-to-play golf operating system
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy,
  Users,
  Target,
  ChevronRight,
  Coins,
  Crown,
  Sparkles,
  Eye,
  MessageCircle,
  Calendar,
  TrendingUp,
  Gift,
  ChevronLeft,
  Clock,
  Building2
} from "lucide-react";
import { Button } from "./ui/button";
import { COIN_EARNING_METHODS } from "./UnlockFeatureModal";
import { CarouselSurface } from './carousel/CarouselSurface';
import { useTop10Summary } from './top10/useTop10Summary';
import { HomeEagleAnimation } from './home-animation/HomeEagleAnimation';
import { LoopingDeck, CarouselArrows, wrapCarouselIndex } from './carousel/LoopingDeck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Premium tier system
const TIERS = {
  bronze: { name: "Bronze", icon: "🥉", minCoins: 0, color: "amber-600" },
  silver: { name: "Silver", icon: "🥈", minCoins: 100, color: "gray-300" },
  gold: { name: "Gold", icon: "🥇", minCoins: 500, color: "amber-400" },
  platinum: { name: "Platinum", icon: "💎", minCoins: 1000, color: "cyan-400" },
  eagle: { name: "Eagle", icon: "🦅", minCoins: 5000, color: "emerald-400" }
};

const getTier = (lifetimeCoins) => {
  if (lifetimeCoins >= 5000) return TIERS.eagle;
  if (lifetimeCoins >= 1000) return TIERS.platinum;
  if (lifetimeCoins >= 500) return TIERS.gold;
  if (lifetimeCoins >= 100) return TIERS.silver;
  return TIERS.bronze;
};

// ============ ONBOARDING POPUP CARDS ============
const OnboardingPopup = ({ onComplete, onCollapseToButtons }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideState, setSlideState] = useState('waiting'); // waiting, entering, visible, exiting, collapsing
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  
  const slides = [
    {
      id: 'tips',
      title: 'QUICK START TIPS',
      icon: <Gift className="w-8 h-8 text-emerald-400" />,
      buttonIcon: <Target className="w-5 h-5" />,
      buttonText: 'Quick Tips',
      buttonColor: 'emerald',
      content: (
        <div className="space-y-3">
          <ul className="space-y-2 text-gray-300">
            <li><span className="text-white font-semibold">Past rounds count!</span> Add any round from your history</li>
            <li><span className="text-white font-semibold">AI Scorecard Reader</span> - Snap a photo to auto-fill scores</li>
            <li><span className="text-white font-semibold">Manual entry</span> - Enter hole-by-hole or just your total</li>
          </ul>
          <div className="pt-3 border-t border-gray-700">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-bold text-lg">+10 coins per round</span>
              <span className="text-gray-500 ml-2">+100 bonus at 3 rounds!</span>
            </div>
          </div>
        </div>
      ),
      gradient: 'from-emerald-500/20 to-emerald-600/5',
      borderColor: 'border-emerald-500/30'
    },
    {
      id: 'coins',
      title: 'EARN GOLF COINS',
      icon: <Gift className="w-8 h-8 text-amber-400" />,
      buttonIcon: <Coins className="w-5 h-5" />,
      buttonText: 'Earn Coins',
      buttonColor: 'amber',
      content: (
        <div className="space-y-3">
          {COIN_EARNING_METHODS.slice(0, 3).map((method, index) => {
            const MethodIcon = method.icon;
            return (
              <div key={index} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <MethodIcon className="w-5 h-5 text-amber-500/70" />
                  <span className="text-gray-300">{method.action}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-400 font-bold text-lg">+{method.coins}</span>
                  <span className="text-xl">🪙</span>
                </div>
              </div>
            );
          })}
        </div>
      ),
      gradient: 'from-amber-500/20 to-amber-600/5',
      borderColor: 'border-amber-500/30'
    }
  ];
  
  const advanceSlide = useCallback(() => {
    setSlideState('exiting');
    setTimeout(() => {
      if (currentSlide < slides.length - 1) {
        setCurrentSlide(prev => prev + 1);
        setSlideState('waiting');
      } else {
        // Last slide - start collapse animation
        setSlideState('collapsing');
        setTimeout(() => {
          onCollapseToButtons();
          onComplete();
        }, 800);
      }
    }, 500);
  }, [currentSlide, slides.length, onComplete, onCollapseToButtons]);
  
  // Initial delay before first card enters
  useEffect(() => {
    const timer = setTimeout(() => setSlideState('entering'), 300);
    return () => clearTimeout(timer);
  }, []);
  
  // Handle slide state transitions
  useEffect(() => {
    if (slideState === 'waiting') {
      const timer = setTimeout(() => setSlideState('entering'), 100);
      return () => clearTimeout(timer);
    }
    if (slideState === 'entering') {
      const timer = setTimeout(() => setSlideState('visible'), 500);
      return () => clearTimeout(timer);
    }
    if (slideState === 'visible') {
      const timer = setTimeout(() => advanceSlide(), 4000);
      return () => clearTimeout(timer);
    }
  }, [slideState, advanceSlide]);
  
  // Touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.touches[0].clientX - dragStartX);
  };
  
  const handleTouchEnd = () => {
    if (dragOffset < -60) advanceSlide();
    setIsDragging(false);
    setDragOffset(0);
  };
  
  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.clientX - dragStartX);
  };
  
  const handleMouseUp = () => {
    if (dragOffset < -60) advanceSlide();
    setIsDragging(false);
    setDragOffset(0);
  };
  
  const currentSlideData = slides[currentSlide] || slides[slides.length - 1];
  
  // Safety check - should never happen but prevents crashes
  if (!currentSlideData) {
    return null;
  }
  
  // Calculate transforms
  const getCardStyle = () => {
    if (slideState === 'collapsing') {
      return {
        transform: 'scale(0.1) rotate(720deg)',
        opacity: 0,
        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
      };
    }
    if (isDragging) {
      return {
        transform: `translateX(${dragOffset}px) rotate(${dragOffset * 0.02}deg)`,
        transition: 'none'
      };
    }
    switch (slideState) {
      case 'waiting':
        return {
          transform: 'translateX(100vw) scale(0.8) rotate(15deg)',
          opacity: 0,
          transition: 'all 0.5s cubic-bezier(0.32, 0.72, 0, 1)'
        };
      case 'entering':
        return {
          transform: 'translateX(0) scale(1) rotate(0deg)',
          opacity: 1,
          transition: 'all 0.5s cubic-bezier(0.32, 0.72, 0, 1)'
        };
      case 'visible':
        return {
          transform: 'translateX(0) scale(1) rotate(0deg)',
          opacity: 1,
          transition: 'all 0.3s ease-out'
        };
      case 'exiting':
        return {
          transform: 'translateX(-100vw) scale(0.8) rotate(-15deg)',
          opacity: 0,
          transition: 'all 0.5s cubic-bezier(0.32, 0.72, 0, 1)'
        };
      default:
        return {};
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
    >
      {/* Skip button */}
      <button 
        onClick={() => {
          setSlideState('collapsing');
          setTimeout(() => {
            onCollapseToButtons();
            onComplete();
          }, 800);
        }}
        className="absolute top-4 right-4 text-gray-500 hover:text-white text-sm transition-colors z-50"
      >
        Skip →
      </button>
      
      {/* Progress counter - replaces dots */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50">
        <span className="text-gray-400 text-sm font-medium">
          {currentSlide + 1} / {slides.length}
        </span>
      </div>
      
      {/* Swipe hint */}
      {slideState === 'visible' && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-500 text-sm animate-pulse z-50">
          ← Swipe to continue
        </div>
      )}
      
      {/* Card */}
      <div
        className="w-full max-w-sm mx-4 select-none cursor-grab active:cursor-grabbing"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div 
          className={`p-5 rounded-2xl bg-gradient-to-br ${currentSlideData.gradient} border ${currentSlideData.borderColor}`}
          style={{
            ...getCardStyle(),
            boxShadow: '0 0 60px rgba(0,0,0,0.6), 0 20px 40px rgba(0,0,0,0.4)'
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className={slideState === 'collapsing' ? 'animate-spin' : 'animate-bounce'}>
              {currentSlideData.icon}
            </div>
            <h2 className="text-lg font-bold text-white tracking-wide">{currentSlideData.title}</h2>
          </div>
          
          {/* Content */}
          {currentSlideData.content}
        </div>
      </div>
    </div>
  );
};

// ============ COLLAPSED QUICK ACTION BUTTONS ============
const QuickActionButtons = ({ onExpandTips, onExpandCoins, isAnimatingIn }) => {
  const [showButtons, setShowButtons] = useState(false);
  
  useEffect(() => {
    if (isAnimatingIn) {
      // Delay button appearance for effect
      const timer = setTimeout(() => setShowButtons(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isAnimatingIn]);
  
  return (
    <div className={`
      flex gap-3 transition-all duration-700
      ${showButtons ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-90'}
    `}>
      <button
        onClick={onExpandTips}
        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl 
          bg-gradient-to-r from-emerald-500/20 to-emerald-600/10 
          border border-emerald-500/30 
          text-emerald-400 font-medium text-sm
          hover:from-emerald-500/30 hover:to-emerald-600/20 
          hover:border-emerald-400/50 hover:scale-105
          active:scale-95
          transition-all duration-200"
      >
        <Target className="w-4 h-4" />
        Quick Tips
      </button>
      
      <button
        onClick={onExpandCoins}
        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl 
          bg-gradient-to-r from-amber-500/20 to-amber-600/10 
          border border-amber-500/30 
          text-amber-400 font-medium text-sm
          hover:from-amber-500/30 hover:to-amber-600/20 
          hover:border-amber-400/50 hover:scale-105
          active:scale-95
          transition-all duration-200"
      >
        <Coins className="w-4 h-4" />
        Earn Coins
      </button>
    </div>
  );
};

// ============ EXPANDABLE TIP MODAL ============
const ExpandableTipModal = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;
  
  const content = type === 'tips' ? (
    <div className="space-y-3">
      <ul className="space-y-2 text-gray-300 text-sm">
        <li><span className="text-white font-semibold">Past rounds count!</span> Add any round from your history</li>
        <li><span className="text-white font-semibold">AI Scorecard Reader</span> - Snap a photo to auto-fill scores</li>
        <li><span className="text-white font-semibold">Manual entry</span> - Enter hole-by-hole or just your total</li>
      </ul>
      <div className="pt-3 border-t border-gray-700">
        <div className="flex items-center gap-2">
          <Coins className="w-4 h-4 text-amber-400" />
          <span className="text-amber-400 font-bold">+10 coins per round</span>
          <span className="text-gray-500 text-sm">+100 bonus at 3 rounds!</span>
        </div>
      </div>
    </div>
  ) : (
    <div className="space-y-2">
      {COIN_EARNING_METHODS.slice(0, 4).map((method, index) => {
        const MethodIcon = method.icon;
        return (
          <div key={index} className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-2">
              <MethodIcon className="w-4 h-4 text-amber-500/70" />
              <span className="text-gray-300 text-sm">{method.action}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-amber-400 font-bold">+{method.coins}</span>
              <span>🪙</span>
            </div>
          </div>
        );
      })}
    </div>
  );
  
  const title = type === 'tips' ? 'QUICK START TIPS' : 'EARN GOLF COINS';
  const gradient = type === 'tips' ? 'from-emerald-500/20 to-emerald-600/5' : 'from-amber-500/20 to-amber-600/5';
  const border = type === 'tips' ? 'border-emerald-500/30' : 'border-amber-500/30';
  const iconColor = type === 'tips' ? 'text-emerald-400' : 'text-amber-400';
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-fade-in"
      onClick={onClose}
    >
      <div 
        className={`ps-themed-dialog ps-theme-panel w-full max-w-sm mx-4 p-5 rounded-2xl ${border} animate-scale-in`}
        data-testid="home-tip-dialog"
        onClick={e => e.stopPropagation()}
        style={{ boxShadow: '0 0 60px rgba(0,0,0,0.6)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gift className={`w-6 h-6 ${iconColor}`} />
            <h3 className="font-bold text-white">{title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        {content}
      </div>
    </div>
  );
};

// ============ GLOW FADE OVERLAY ============
const GlowFadeOverlay = ({ isActive }) => {
  return (
    <div 
      className={`
        fixed inset-0 z-30 pointer-events-none
        transition-all duration-1000 ease-out
        ${isActive ? 'opacity-100' : 'opacity-0'}
      `}
      style={{
        background: isActive 
          ? 'radial-gradient(circle at center, rgba(34, 197, 94, 0.15) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,1) 100%)'
          : 'transparent'
      }}
    />
  );
};

// ============ PERSISTENT FUNCTION CAROUSEL ============
export const FunctionCarousel = ({ isVisible, onDismiss, navigate, discoverTeaser, onExpandTips, onExpandCoins }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const top10 = useTop10Summary(isVisible);
  const audioContextRef = useRef(null);
  
  // All function cards - including greyed out/coming soon
  const functionCards = [
    {
      id: 'log-round',
      title: 'Log Round',
      subtitle: 'Track your scores',
      icon: <Target className="w-12 h-12 text-emerald-400" />,
      gradient: 'from-emerald-900/90 to-emerald-800/50',
      border: 'border-emerald-500/60',
      glowColor: 'rgba(16, 185, 129, 0.6)',
      enabled: true,
      onClick: () => { onDismiss(); navigate('/add-score'); }
    },
    {
      id: 'top10', title: 'Top10',
      subtitle: top10?.trophy ? 'Local Player · Complete' : ['active','completed'].includes(top10?.status) ? `${top10.completed_count}/10 public courses played` : 'Your 50-mile public-course challenge',
      badge: '500 COINS', badgeColor: 'bg-amber-100 text-amber-950',
      icon: <Trophy className="w-12 h-12 text-yellow-400" />,
      gradient: 'from-emerald-900/90 to-green-800/50', border: 'border-yellow-500/60',
      glowColor: 'rgba(16, 185, 129, 0.6)', enabled: true,
      onClick: () => { onDismiss(); navigate('/top10'); }
    },
    {
      id: '19th',
      title: '19th',
      subtitle: 'Golf stories & updates',
      icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
      gradient: 'from-cyan-900/90 to-cyan-800/50',
      border: 'border-cyan-500/60',
      glowColor: 'rgba(6, 182, 212, 0.6)',
      enabled: true,
      onClick: () => { onDismiss(); navigate('/feed'); }
    },
    {
      id: 'ps-members',
      title: 'PS Golfers',
      subtitle: `${discoverTeaser?.count || 5} golfers nearby`,
      icon: <Users className="w-12 h-12 text-emerald-400" />,
      gradient: 'from-emerald-900/90 to-green-800/50',
      border: 'border-emerald-500/60',
      glowColor: 'rgba(16, 185, 129, 0.6)',
      enabled: true,
      onClick: () => { onDismiss(); navigate('/discover'); }
    },
    {
      id: 'leaderboard',
      title: 'PS Leaderboard',
      subtitle: 'Rankings & Champions',
      badge: '🏆',
      badgeColor: 'bg-yellow-500/40 text-yellow-300',
      icon: <Trophy className="w-12 h-12 text-yellow-400" />,
      gradient: 'from-yellow-900/90 to-amber-800/50',
      border: 'border-yellow-500/60',
      glowColor: 'rgba(234, 179, 8, 0.6)',
      enabled: true,
      onClick: () => { onDismiss(); navigate('/leaderboard'); }
    },
    {
      id: 'compete',
      title: 'Compete',
      subtitle: 'Match Play • Skins • Stroke',
      badge: '💰 PRIZES',
      badgeColor: 'bg-amber-500/40 text-amber-300',
      icon: <Trophy className="w-12 h-12 text-amber-400" />,
      gradient: 'from-amber-900/90 to-yellow-800/50',
      border: 'border-amber-500/60',
      glowColor: 'rgba(245, 158, 11, 0.6)',
      coinCost: 'Entry fees',
      enabled: true,
      onClick: () => { onDismiss(); navigate('/tournaments'); }
    },
    {
      id: 'events',
      title: 'Golf Events',
      subtitle: 'Outings & Tournaments',
      icon: <Calendar className="w-12 h-12 text-purple-400" />,
      gradient: 'from-purple-900/90 to-purple-800/50',
      border: 'border-purple-500/60',
      glowColor: 'rgba(168, 85, 247, 0.6)',
      enabled: true,
      onClick: () => { onDismiss(); navigate('/events'); }
    },
    {
      id: 'tee-times',
      title: 'Book Tee Times',
      subtitle: 'Coming Soon',
      badge: 'SOON',
      badgeColor: 'bg-gray-600 text-gray-300',
      icon: <Clock className="w-12 h-12 text-gray-500" />,
      gradient: 'from-gray-800/90 to-gray-900/50',
      border: 'border-gray-600/40',
      glowColor: 'rgba(100, 100, 100, 0.3)',
      enabled: true,
      onClick: () => { onDismiss(); navigate('/book'); }
    },
    {
      id: 'quick-tips',
      title: 'Quick Tips',
      subtitle: 'Get started fast',
      icon: <Sparkles className="w-12 h-12 text-cyan-400" />,
      gradient: 'from-cyan-900/90 to-cyan-800/50',
      border: 'border-cyan-500/60',
      glowColor: 'rgba(6, 182, 212, 0.6)',
      enabled: true,
      onClick: onExpandTips
    },
    {
      id: 'earn-coins',
      title: 'Earn Coins',
      subtitle: 'Multiple ways to earn',
      icon: <Coins className="w-12 h-12 text-amber-400" />,
      gradient: 'from-amber-900/90 to-amber-800/50',
      border: 'border-amber-500/60',
      glowColor: 'rgba(245, 158, 11, 0.6)',
      enabled: true,
      onClick: onExpandCoins
    }
  ];

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Resume audio context if suspended (required for mobile browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(250, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(60, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.06, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Sound is an enhancement, silently fail if not supported
    }
  }, []);

  // Navigate to card
  const goToCard = useCallback((index) => {
    const next = wrapCarouselIndex(index, functionCards.length);
    if (next !== currentIndex) {
      playSwoosh();
      setCurrentIndex(next);
    }
  }, [currentIndex, functionCards.length, playSwoosh]);
  return <CarouselSurface id="home-carousel" label="Feature menu" open={isVisible} onExit={onDismiss} plain>
    <LoopingDeck id="home-feature-deck" actionCards items={functionCards} index={currentIndex} onIndexChange={goToCard}
      renderItem={(card, active) => <button type="button" data-testid={`home-feature-${card.id}`}
        onClick={() => { if (active && card.enabled) card.onClick(); }} disabled={!card.enabled}
        className={`carousel-action-card bg-gradient-to-br ${card.gradient} ${card.border}`}
        style={{ boxShadow: active ? `0 10px 30px ${card.glowColor.replace('0.6', '0.22')}` : 'none' }}>
        <div className="flex flex-col items-center text-center justify-center gap-2">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-black/30">{card.icon}</div>
          <div className="flex flex-wrap justify-center items-center gap-2">
            <h3 className="font-bold text-lg text-white" data-testid={`home-feature-title-${card.id}`}>{card.title}</h3>
            {card.badge && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${card.badgeColor}`}>{card.badge}</span>}
          </div>
          <p className="text-sm text-gray-300">{card.subtitle}</p>
          {card.coinCost && <span className="text-sm text-amber-400">{card.coinCost}</span>}
        </div>
      </button>} />
    <CarouselArrows id="home-carousel" previous={() => goToCard(currentIndex - 1)} next={() => goToCard(currentIndex + 1)} />
  </CarouselSurface>;
};

const PremiumDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    coinsBalance: 0,
    lifetimeCoins: 0
  });
  const [discoverTeaser, setDiscoverTeaser] = useState({ count: 0, online: 0 });
  
  // Home remains visible; the feature carousel opens only on request.
  const [showGlowFade, setShowGlowFade] = useState(false);
  const [dashboardRevealed, setDashboardRevealed] = useState(true);
  
  // Expandable modal state
  const [expandedModal, setExpandedModal] = useState(null); // 'tips' | 'coins' | null
  
  const [showCarousel, setShowCarousel] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch multiple data points in parallel
      const [coinsRes, discoverRes] = await Promise.all([
        axios.get(`${API}/golf-coins/balance`).catch(() => ({ data: { balance: 0, lifetime_earned: 0 } })),
        axios.get(`${API}/golfers/discover?limit=5`).catch(() => ({ data: { total: 0, online_count: 0 } }))
      ]);
      
      setStats({
        coinsBalance: coinsRes.data.balance || 0,
        lifetimeCoins: coinsRes.data.lifetime_earned || 0
      });
      
      setDiscoverTeaser({
        count: discoverRes.data.total || 15,
        online: discoverRes.data.online_count || 3
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="ps-theme-page flex items-center justify-center min-h-[60vh]" data-testid="premium-home-loading">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentTier = getTier(stats.lifetimeCoins);

  return (
    <>
      {/* Glow Fade Overlay */}
      <GlowFadeOverlay isActive={showGlowFade} />
      
      {/* Main Dashboard */}
      <div 
        className={`
          ps-theme-page min-h-screen pb-24
          transition-all duration-1000
          ${dashboardRevealed ? 'opacity-100' : 'opacity-0'}
        `}
        data-testid="premium-home-page"
      >
      {/* Premium Header */}
      <div className="ps-page-header pt-6 pb-8 px-4" data-testid="premium-home-header">
        <div className="max-w-lg mx-auto">
          
          <HomeEagleAnimation userId={user?.user_id} />

          <button
            type="button"
            onClick={() => setShowCarousel(true)}
            data-testid="open-feature-menu"
            aria-haspopup="dialog"
            aria-expanded={showCarousel}
            className="ps-home-feature w-full min-h-14 rounded-2xl p-4 mb-3 transition-colors"
            style={{ boxShadow: '0 0 30px rgba(16, 185, 129, 0.15)' }}
          >
            <span className="flex items-center justify-center gap-3">
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">Scroll Feature</span>
              <ChevronRight className="w-5 h-5" />
            </span>
          </button>

          {/* Enhanced Info - Past Rounds & AI Scorecard */}
          <div className="ps-page-surface border rounded-xl p-3" data-testid="home-round-tips">
            <div className="ps-home-tip-row mb-2">
              <div className="text-emerald-400 text-sm font-medium">
                <Coins className="w-4 h-4 inline mr-1" />
                Earn 10 coins per round
              </div>
              <span className="text-gray-600">|</span>
              <div className="text-amber-400 text-sm">
                +100 bonus at 3 rounds!
              </div>
            </div>
            <p className="text-gray-500 text-xs">
              💡 <strong className="text-gray-400">Tip:</strong> Add past rounds too! Use AI Scorecard Reader or manual entry.
            </p>
          </div>
        </div>
      </div>
      
      {/* Expandable Tip Modals */}
      <ExpandableTipModal 
        isOpen={expandedModal !== null}
        onClose={() => setExpandedModal(null)}
        type={expandedModal}
      />

      {/* On-demand feature carousel; startup splash and login remain unchanged. */}
      <FunctionCarousel
        isVisible={showCarousel}
        onDismiss={() => setShowCarousel(false)}
        navigate={navigate}
        discoverTeaser={discoverTeaser}
        onExpandTips={() => { setShowCarousel(false); setExpandedModal('tips'); }}
        onExpandCoins={() => { setShowCarousel(false); setExpandedModal('coins'); }}
      />

      {/* Buy Coins CTA */}
      <div className="px-4 py-4">
        <div className="max-w-lg mx-auto">
          <div className="ps-page-surface border rounded-2xl p-5" data-testid="home-buy-coins-panel">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold">Need More Coins?</h3>
                <p className="text-gray-400 text-sm">Unlock premium features instantly</p>
              </div>
              <Button
                onClick={() => navigate('/golf-coins?tab=buy')}
                className="ps-home-buy px-4"
                data-testid="home-buy-coins-button"
              >
                Buy Coins
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tier Progress */}
      <div className="px-4 py-4">
        <div className="max-w-lg mx-auto">
          <div className="ps-page-surface border rounded-2xl p-5" data-testid="home-membership-status">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Your Status</h3>
              <div className="flex items-center gap-1">
                <span className="text-xl">{currentTier.icon}</span>
                <span className="text-gray-400">{currentTier.name}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-gray-400 text-sm">Lifetime coins: {stats.lifetimeCoins}</span>
            </div>
            {/* Next tier progress */}
            {currentTier !== TIERS.eagle && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{currentTier.name}</span>
                  <span>Next tier</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full transition-all"
                    style={{ 
                      width: `${Math.min(100, (stats.lifetimeCoins / (
                        currentTier === TIERS.bronze ? 100 :
                        currentTier === TIERS.silver ? 500 :
                        currentTier === TIERS.gold ? 1000 :
                        5000
                      )) * 100)}%` 
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default PremiumDashboard;
